import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuthStore } from '../hooks/useAuth'

function clr(v: number) { return v >= 0 ? 'var(--cash)' : 'var(--red)' }

// ─── Free tier demo ───────────────────────────────────────────────────────────
const DEMO_ITEMS = [
  { ticker: 'AAPL',  price: 187.42, change: 1.23,  changePct: '+0.66%', high: 188.91, low: 185.30, notes: 'Watching OB retest at 185–186' },
  { ticker: 'NVDA',  price: 824.18, change: -11.40, changePct: '-1.37%', high: 836.50, low: 818.20, notes: 'HTF bullish, waiting for discount' },
  { ticker: 'SPY',   price: 521.34, change: 0.88,  changePct: '+0.17%', high: 523.10, low: 519.80, notes: null },
  { ticker: 'MSFT',  price: 412.60, change: 3.14,  changePct: '+0.77%', high: 414.20, low: 409.50, notes: 'Near all-time highs, watching for rejection' },
]

function FreePreview({ onUpgrade }: { onUpgrade: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.3)', borderRadius: 3, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <img src="/icons/watchlist.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/>
        <div style={{ flex: 1, fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--amber)', lineHeight: 1.5 }}>
          <strong>Preview mode</strong> — this is a sample watchlist. Upgrade to track your own tickers with live quotes.
        </div>
        <button onClick={onUpgrade} style={{ padding: '6px 16px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Upgrade — $9.99/mo →
        </button>
      </div>

      {/* Disabled add bar */}
      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '14px 16px', display: 'flex', gap: 8, opacity: .35, pointerEvents: 'none', userSelect: 'none' }}>
        <div style={{ flex: '0 0 100px' }}>
          <div className="eyebrow" style={{ marginBottom: 5 }}>Ticker</div>
          <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 9px', fontFamily: 'Syne Mono', fontSize: '.75rem', color: 'var(--muted)' }}>AAPL</div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="eyebrow" style={{ marginBottom: 5 }}>Notes (optional)</div>
          <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 9px', fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--muted)' }}>Add a note...</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ padding: '7px 16px', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', color: 'var(--muted)' }}>Add</div>
        </div>
      </div>

      {/* Demo tickers */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {DEMO_ITEMS.map((item, i) => (
          <div key={item.ticker} className="wl-row" style={{
            filter: i >= 2 ? 'blur(3px)' : 'none',
            opacity: i >= 2 ? 0.45 : 1,
            pointerEvents: 'none', userSelect: 'none',
          }}>
            <div className="wl-ticker">{item.ticker}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="wl-price-block">
                <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.95rem', color: clr(item.change), whiteSpace: 'nowrap' }}>${item.price.toFixed(2)}</span>
                <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: clr(item.change), whiteSpace: 'nowrap' }}>{item.change >= 0 ? '+' : ''}{item.change.toFixed(2)} ({item.changePct})</span>
                <span style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', whiteSpace: 'nowrap' }}>H:{item.high.toFixed(2)} L:{item.low.toFixed(2)}</span>
              </div>
              {item.notes && <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', marginTop: 3, opacity: .7 }}>{item.notes}</div>}
            </div>
            <div className="wl-actions">
              <div style={{ padding: '5px 10px', background: 'var(--stamp)', border: '1px solid var(--cash-dim)', borderRadius: 2, color: 'var(--cash)', fontFamily: 'Syne Mono', fontSize: '.56rem' }}>Analyze</div>
              <div style={{ padding: '5px 10px', background: 'transparent', border: '1px solid var(--edge)', borderRadius: 2, color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.56rem' }}>+ Alert</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '22px', textAlign: 'center' }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.88rem', color: 'var(--bill)', marginBottom: 8 }}>Track your own tickers</div>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)', marginBottom: 16, lineHeight: 1.7 }}>
          Add any ticker, set price alerts at key ICT levels, and see live quotes — all included with Pro.
        </div>
        <button onClick={onUpgrade} style={{ padding: '10px 28px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.8rem', cursor: 'pointer' }}>
          Get Pro — $9.99/mo →
        </button>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--muted)', marginTop: 8, opacity: .6 }}>Cancel anytime</div>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Watchlist() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const isFree = !user || user.tier === 'free'

  const [input,       setInput]       = useState('')
  const [notes,       setNotes]       = useState('')
  const [alertTicker, setAlertTicker] = useState('')
  const [alertType,   setAlertType]   = useState<'above' | 'below'>('above')
  const [alertPrice,  setAlertPrice]  = useState('')
  const [alertNote,   setAlertNote]   = useState('')
  const [tab,         setTab]         = useState<'watchlist' | 'alerts'>('watchlist')
  const [error,       setError]       = useState('')

  const { data: wlData, isLoading } = useQuery({
    queryKey: ['watchlist'],
    queryFn: () => api.watchlist.list(),
    enabled: !isFree,
  })
  const { data: quotesData, isLoading: quotesLoading } = useQuery({
    queryKey: ['watchlist-quotes'],
    queryFn: () => api.watchlist.quotes(),
    refetchInterval: 60_000,
    enabled: !isFree,
  })
  const { data: alertsData } = useQuery({
    queryKey: ['watchlist-alerts'],
    queryFn: () => api.watchlist.alerts(),
    enabled: !isFree && tab === 'alerts',
  })

  const addMutation = useMutation({
    mutationFn: (data: { ticker: string; notes?: string }) => api.watchlist.add(data.ticker, data.notes),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['watchlist'] }); qc.invalidateQueries({ queryKey: ['watchlist-quotes'] }); setInput(''); setNotes(''); setError('') },
    onError: (e: any) => setError(e.message),
  })
  const removeMutation = useMutation({
    mutationFn: (ticker: string) => api.watchlist.remove(ticker),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['watchlist'] }); qc.invalidateQueries({ queryKey: ['watchlist-quotes'] }) },
  })
  const addAlertMutation = useMutation({
    mutationFn: (data: any) => api.watchlist.addAlert(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['watchlist-alerts'] }); setAlertPrice(''); setAlertNote(''); setError('') },
    onError: (e: any) => setError(e.message),
  })
  const deleteAlertMutation = useMutation({
    mutationFn: (id: string) => api.watchlist.deleteAlert(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist-alerts'] }),
  })

  const items  = wlData?.items  ?? []
  const quotes = quotesData?.quotes ?? []
  const alerts = alertsData?.alerts ?? []

  const quoteMap: Record<string, any> = {}
  for (const q of quotes) { if (q.quote) quoteMap[q.ticker] = q.quote }

  function submitAlert() {
    if (!alertTicker || !alertPrice) { setError('Ticker and price required'); return }
    addAlertMutation.mutate({ ticker: alertTicker.toUpperCase(), alertType, price: parseFloat(alertPrice), note: alertNote || undefined })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Watchlist</h1>
          <div className="eyebrow">Track tickers and set price alerts{isFree ? ' · Preview mode' : ''}</div>
        </div>
        {!isFree && (
          <div style={{ display: 'flex', border: '1px solid var(--edge)', borderRadius: 3, overflow: 'hidden' }}>
            {(['watchlist', 'alerts'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                padding: '7px 16px', background: tab === t ? 'var(--stamp)' : 'transparent',
                border: 'none', borderBottom: tab === t ? '2px solid var(--cash)' : '2px solid transparent',
                color: tab === t ? 'var(--cash)' : 'var(--muted)',
                fontFamily: 'Syne', fontWeight: 600, fontSize: '.72rem', cursor: 'pointer',
              }}>{ t === 'watchlist' ? <span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/watchlist.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> Watchlist ({items.length})</span> : <span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/alerts_bell.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> Alerts ({alerts.filter((a: any) => !a.triggered).length})</span>}</button>
            ))}
          </div>
        )}
      </div>

      {/* Free tier preview */}
      {isFree && <FreePreview onUpgrade={() => navigate('/pricing')} />}

      {/* Watchlist tab */}
      {!isFree && tab === 'watchlist' && (
        <>
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '14px 16px', marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '0 0 100px' }}>
              <div className="eyebrow" style={{ marginBottom: 5 }}>Ticker</div>
              <input value={input} onChange={e => setInput(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && addMutation.mutate({ ticker: input, notes: notes || undefined })} placeholder="AAPL" maxLength={10}
                style={{ width: '100%', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 9px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.75rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div className="eyebrow" style={{ marginBottom: 5 }}>Notes (optional)</div>
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Watching for OB retest..." maxLength={200}
                style={{ width: '100%', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 9px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.72rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <button onClick={() => addMutation.mutate({ ticker: input, notes: notes || undefined })} disabled={!input || addMutation.isPending}
              style={{ padding: '7px 16px', background: input ? 'var(--cash)' : 'var(--paper)', color: input ? 'var(--ink)' : 'var(--muted)', border: '1px solid var(--edge)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', cursor: input ? 'pointer' : 'default' }}>
              {addMutation.isPending ? '...' : 'Add'}
            </button>
            {error && <div style={{ width: '100%', fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--red)' }}>✗ {error}</div>}
          </div>

          {isLoading ? (
            <div style={{ padding: 32, textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)' }}>Loading...</div>
          ) : items.length === 0 ? (
            <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '36px', textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)' }}>
              Add tickers above to start tracking them
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map((item: any) => {
                const q = quoteMap[item.ticker]
                const change = q?.change ?? null
                return (
                  <div key={item.ticker} className="wl-row">
                    <div className="wl-ticker">{item.ticker}</div>
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      {q ? (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                          <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', color: clr(change), whiteSpace: 'nowrap', flexShrink: 0 }}>${q.price?.toFixed(2)}</span>
                          <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: clr(change), whiteSpace: 'nowrap' }}>{change >= 0 ? '+' : ''}{change?.toFixed(2)} ({q.changePct})</span>
                          <span style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', whiteSpace: 'nowrap' }}>H:{q.high?.toFixed(2)} L:{q.low?.toFixed(2)}</span>
                        </div>
                      ) : quotesLoading ? (
                        <span style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>Loading...</span>
                      ) : (
                        <span style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>—</span>
                      )}
                      {item.notes && <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', marginTop: 3, opacity: .7 }}>{item.notes}</div>}
                      {item.alerts?.length > 0 && <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--amber)', marginTop: 2, display:'flex', alignItems:'center', gap:3 }}><img src="/icons/alerts_bell.svg" alt="" style={{width:10,height:10}}/> {item.alerts.length} alert{item.alerts.length > 1 ? 's' : ''}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button onClick={() => navigate('/analyze', { state: { ticker: item.ticker, mode: 'ticker' } })} style={{ padding: '5px 10px', background: 'var(--stamp)', border: '1px solid var(--cash-dim)', borderRadius: 2, color: 'var(--cash)', fontFamily: 'Syne Mono', fontSize: '.56rem', cursor: 'pointer' }}>Analyze</button>
                      <button onClick={() => { setAlertTicker(item.ticker); setTab('alerts') }} style={{ padding: '5px 10px', background: 'transparent', border: '1px solid var(--edge)', borderRadius: 2, color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.56rem', cursor: 'pointer' }}>+ Alert</button>
                      <button onClick={() => removeMutation.mutate(item.ticker)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '.7rem', padding: '2px 6px' }}>×</button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* Alerts tab */}
      {!isFree && tab === 'alerts' && (
        <>
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '14px 16px', marginBottom: 14 }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>New Alert</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <div className="eyebrow" style={{ marginBottom: 4 }}>Ticker</div>
                <input value={alertTicker} onChange={e => setAlertTicker(e.target.value.toUpperCase())} placeholder="AAPL" maxLength={10}
                  style={{ width: 90, background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 9px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.72rem', outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {(['above', 'below'] as const).map(t => (
                  <button key={t} onClick={() => setAlertType(t)} style={{
                    padding: '7px 12px', border: `1px solid ${alertType === t ? 'var(--cash-dim)' : 'var(--edge)'}`,
                    background: alertType === t ? 'var(--stamp)' : 'transparent',
                    color: alertType === t ? 'var(--cash)' : 'var(--muted)',
                    fontFamily: 'Syne Mono', fontSize: '.62rem', borderRadius: 2, cursor: 'pointer',
                  }}>{t}</button>
                ))}
              </div>
              <div>
                <div className="eyebrow" style={{ marginBottom: 4 }}>Price</div>
                <input value={alertPrice} onChange={e => setAlertPrice(e.target.value)} placeholder="190.00" type="number" step="0.01"
                  style={{ width: 100, background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 9px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.72rem', outline: 'none' }} />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div className="eyebrow" style={{ marginBottom: 4 }}>Note (optional)</div>
                <input value={alertNote} onChange={e => setAlertNote(e.target.value)} placeholder="OB level, key S/R..." maxLength={200}
                  style={{ width: '100%', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 9px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.72rem', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <button onClick={submitAlert} disabled={addAlertMutation.isPending}
                style={{ padding: '7px 16px', background: alertTicker && alertPrice ? 'var(--cash)' : 'var(--paper)', color: alertTicker && alertPrice ? 'var(--ink)' : 'var(--muted)', border: '1px solid var(--edge)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', cursor: 'pointer' }}>
                {addAlertMutation.isPending ? '...' : 'Set Alert'}
              </button>
            </div>
            {error && <div style={{ marginTop: 8, fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--red)' }}>✗ {error}</div>}
            <div style={{ marginTop: 10, fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', opacity: .7 }}>
              Alerts are checked when you visit — real-time push notifications coming soon
            </div>
          </div>

          {alerts.length === 0 ? (
            <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '28px', textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)' }}>No alerts set</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {alerts.map((a: any) => (
                <div key={a.id} style={{ background: 'var(--card)', border: `1px solid ${a.triggered ? 'rgba(133,196,124,.3)' : 'var(--edge)'}`, borderRadius: 3, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', width: 56 }}>{a.ticker}</div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', flex: 1 }}>
                    <span style={{ color: a.alertType === 'above' ? 'var(--cash)' : 'var(--red)', textTransform: 'uppercase', marginRight: 6 }}>{a.alertType}</span>
                    ${a.price}
                    {a.note && <span style={{ color: 'var(--muted)', marginLeft: 10 }}>{a.note}</span>}
                  </div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: a.triggered ? 'var(--cash)' : 'var(--amber)', textTransform: 'uppercase' }}>{a.triggered ? '✓ Triggered' : 'Watching'}</div>
                  <button onClick={() => deleteAlertMutation.mutate(a.id)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '.7rem', padding: '2px 4px' }}>×</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
